import { useCallback, useMemo } from "react";
import type { LucideIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useCountry } from "@/config/country-context";
import { matchOptionValue } from "@/features/refinance/utils/match-option-value";
import { DEBT_TYPE_ICONS } from "../utils/debt-type-icons";
import {
	DEBT_TYPES,
	DEBT_TYPES_BY_COUNTRY,
	type DebtType,
	isDebtType,
} from "../utils/debt-types";

export const useDebtTypeOptions = () => {
	const t = useTranslations("pages.ConfirmDebtPage.debtTypes");
	const country = useCountry();

	const allOptions = useMemo(
		() => DEBT_TYPES.map((value) => ({ value, label: t(value) })),
		[t]
	);

	const selectOptions = useMemo(
		() =>
			DEBT_TYPES_BY_COUNTRY[country].map((value) => ({
				value,
				label: t(value),
			})),
		[country, t]
	);

	const typeFor = useCallback(
		(raw: string | undefined): DebtType | undefined => {
			const matched = matchOptionValue(raw?.trim() ?? "", allOptions);
			return isDebtType(matched) ? matched : undefined;
		},
		[allOptions]
	);

	/** A register-supplied type we cannot match is shown as reported. */
	const labelFor = useCallback(
		(raw: string | undefined): string => {
			const type = typeFor(raw);
			return type ? t(type) : (raw?.trim() ?? "");
		},
		[t, typeFor]
	);

	/** A type we cannot match gets no icon, the same fallback as its label. */
	const iconFor = useCallback(
		(raw: string | undefined): LucideIcon | undefined => {
			const type = typeFor(raw);
			return type ? DEBT_TYPE_ICONS[type] : undefined;
		},
		[typeFor]
	);

	return { selectOptions, labelFor, iconFor };
};
